import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, Users, CreditCard, ScrollText, Leaf, MessageSquare, Trophy, Receipt,
  Settings, LogOut, Search, Ban, CheckCircle2, XCircle, Send, Sparkles, Loader2,
  Wand2, Play, BadgeCheck, Plus, Trash2, IndianRupee, TrendingUp, UserPlus, ShieldCheck, HelpCircle, Phone,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { StarField } from "@/components/StarField";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["overview", "Overview", LayoutGrid],
  ["users", "Users", Users],
  ["subscriptions", "Subscriptions", CreditCard],
  ["plans", "Plans", IndianRupee],
  ["predictions", "Predictions", ScrollText],
  ["questions", "Questions", HelpCircle],
  ["consultations", "Consultations", Phone],
  ["astrologers", "Astrologers", UserPlus],
  ["remedies", "Remedies", Leaf],
  ["communications", "Communications", MessageSquare],
  ["luckydraw", "Lucky Draw", Trophy],
  ["payments", "Payments", Receipt],
  ["config", "Config", Settings],
];

export default function Admin() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-[#080B1A] text-slate-100 flex">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-amber-500/12 bg-[#0A0E20] fixed inset-y-0">
        <div className="px-5 py-5 border-b border-amber-500/12"><Logo /></div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(([k, label, Icon]) => (
            <button key={k} data-testid={`admin-nav-${k}`} onClick={() => setTab(k)}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${tab === k ? "bg-[#1A2247] text-amber-100 border border-amber-400/30" : "text-slate-400 hover:text-amber-200"}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-amber-500/12 space-y-1">
          <button onClick={() => nav("/")} className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:text-amber-200"><LayoutGrid size={17} /> View Site</button>
          <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:text-red-400"><LogOut size={17} /> Log out</button>
        </div>
      </aside>

      <main className="flex-1 md:ml-60 relative">
        <StarField count={30} />
        {/* mobile nav */}
        <div className="md:hidden sticky top-0 z-30 glass border-b border-amber-500/15 px-4 py-3 flex items-center justify-between">
          <Logo textClass="text-lg" iconSize={18} />
          <select value={tab} onChange={(e) => setTab(e.target.value)} className="bg-[#0B1024] border border-amber-500/25 rounded-lg px-2 py-1.5 text-sm">
            {NAV.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div className="relative z-10 p-4 sm:p-8 max-w-6xl mx-auto">
          {tab === "overview" && <Overview />}
          {tab === "users" && <UsersTab />}
          {tab === "subscriptions" && <SubscriptionsTab />}
          {tab === "predictions" && <PredictionsTab />}
          {tab === "remedies" && <RemediesTab />}
          {tab === "communications" && <CommunicationsTab />}
          {tab === "luckydraw" && <LuckyDrawTab />}
          {tab === "payments" && <PaymentsTab />}
          {tab === "config" && <ConfigTab />}
          {tab === "plans" && <PlansTab />}
          {tab === "questions" && <QuestionsTab />}
          {tab === "consultations" && <ConsultationsTab />}
          {tab === "astrologers" && <AstrologersTab />}
        </div>
      </main>
    </div>
  );
}

function Head({ title, sub }) {
  return (
    <div className="mb-6">
      <h1 className="font-serif text-3xl text-slate-50">{title}</h1>
      {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
    </div>
  );
}

function Card({ children, className = "", ...p }) {
  return <div className={`mc-card p-5 ${className}`} {...p}>{children}</div>;
}

function Overview() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/reports").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return <Loading />;
  const stats = [
    ["Total Users", r.total_users, Users],
    ["New (30d)", r.new_users_30d, UserPlus],
    ["Active Subscribers", r.active_subscribers, ShieldCheck],
    ["MRR", `₹${r.mrr.toLocaleString("en-IN")}`, TrendingUp],
    ["Total Revenue", `₹${r.total_revenue.toLocaleString("en-IN")}`, IndianRupee],
    ["Churn Rate", `${r.churn_rate}%`, XCircle],
    ["24-Month Eligible", r.eligible_24m, Trophy],
    ["Predictions Delivered", r.predictions_delivered, Send],
  ];
  return (
    <>
      <Head title="Reports & Overview" sub="Key metrics across MyssticChat" />
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="admin-overview-stats">
        {stats.map(([label, val, Icon]) => (
          <Card key={label} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <Icon size={18} className="text-amber-300" />
            <p className="mt-3 font-serif text-3xl text-slate-50">{val}</p>
            <p className="text-xs text-slate-400 mt-1">{label}</p>
          </Card>
        ))}
      </div>
    </>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data)), [q]);
  useEffect(() => { load(); }, [load]);
  const suspend = async (id) => {
    try { const { data } = await api.post(`/admin/users/${id}/suspend`); toast.success(data.suspended ? "User suspended" : "User reactivated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Head title="Users" sub={`${users.length} registered users`} />
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone"
          className="w-full rounded-xl bg-[#0B1024] border border-amber-500/20 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-amber-400/60" />
      </div>
      <div className="space-y-3" data-testid="admin-users-table">
        {users.map((u) => (
          <Card key={u.id} data-testid={`admin-user-${u.email}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-medium text-slate-100">{u.name} {u.suspended && <span className="text-xs text-red-400">(suspended)</span>}</p>
                <p className="text-sm text-slate-400">{u.email} · {u.phone}</p>
                {u.birth_profile && <p className="text-xs text-slate-500 mt-1">DOB {u.birth_profile.date_of_birth} · {u.birth_profile.birth_city} · {u.birth_profile.sun_sign}</p>}
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span>Sub: {u.subscription?.status || "none"}</span>
                  {u.comm_preferences && <span>· WA: {u.comm_preferences.whatsapp_opt_in ? "yes" : "no"} · Email: {u.comm_preferences.email_opt_in ? "yes" : "no"}</span>}
                </div>
              </div>
              <button data-testid={`admin-suspend-${u.email}`} onClick={() => suspend(u.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-xs flex items-center gap-1.5 ${u.suspended ? "btn-ghost-gold" : "border border-red-500/30 text-red-300 hover:bg-red-500/10"}`}>
                <Ban size={14} /> {u.suspended ? "Reactivate" : "Suspend"}
              </button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/admin/subscriptions").then((r) => setSubs(r.data)); }, []);
  return (
    <>
      <Head title="Subscriptions" sub={`${subs.filter((s) => s.status === "active").length} active`} />
      <div className="overflow-x-auto mc-card p-0" data-testid="admin-subscriptions-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-amber-400/80 border-b border-amber-500/15">
            <Th>User</Th><Th>Status</Th><Th>Start</Th><Th>Renewal</Th><Th>Months</Th></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} className="border-b border-amber-500/8">
                <Td><span className="text-slate-200">{s.user_name}</span><br /><span className="text-xs text-slate-500">{s.user_email}</span></Td>
                <Td><StatusPill s={s.status} /></Td>
                <Td>{fmtDate(s.start_date)}</Td>
                <Td>{fmtDate(s.renewal_date)}</Td>
                <Td>{s.consecutive_paid_months}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PredictionsTab() {
  const [preds, setPreds] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [genUser, setGenUser] = useState("");
  const load = () => api.get("/admin/predictions").then((r) => setPreds(r.data));
  useEffect(() => { load(); api.get("/admin/users").then((r) => setUsers(r.data)); }, []);

  const generate = async () => {
    if (!genUser) { toast.error("Select a user first"); return; }
    setBusy("gen");
    try {
      await api.post("/admin/predictions/generate", { user_id: genUser });
      toast.success("AI prediction drafted — review & approve below");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const generateDeliver = async () => {
    if (!genUser) { toast.error("Select a user first"); return; }
    setBusy("gd");
    try {
      await api.post("/admin/predictions/generate-deliver", { user_id: genUser });
      toast.success("Generated with chart data & delivered (email + WhatsApp)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/predictions/${id}/${action}`);
      toast.success(`Prediction ${action}d`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <>
      <Head title="Prediction Engine" sub="Generate with AI, review, approve and deliver" />
      <Card className="mb-5" data-testid="admin-generate-panel">
        <div className="flex items-center gap-2 text-amber-300 mb-3"><Wand2 size={17} /><span className="text-xs uppercase tracking-[0.15em]">AI-Assisted Generation</span></div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select data-testid="admin-gen-user-select" value={genUser} onChange={(e) => setGenUser(e.target.value)}
            className="flex-1 rounded-xl bg-[#0B1024] border border-amber-500/20 px-4 py-2.5 text-sm">
            <option value="">Select a subscriber…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
          <button data-testid="admin-generate-button" onClick={generate} disabled={busy === "gen"}
            className="btn-ghost-gold rounded-full px-6 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "gen" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft
          </button>
          <button data-testid="admin-generate-deliver-button" onClick={generateDeliver} disabled={busy === "gd"}
            className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "gd" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Generate &amp; Deliver
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500">Uses real birth-chart data (when an astrology API key is set) + AI writing. AI-generated astrology is not scientifically proven. "Draft" needs approval; "Generate &amp; Deliver" sends immediately. Auto-runs for all active subscribers on the <span className="text-amber-300">5th of every month</span>.</p>
      </Card>

      <div className="space-y-3" data-testid="admin-predictions-list">
        {preds.map((p) => (
          <Card key={p.id} data-testid={`admin-prediction-${p.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-100">{p.month} {p.year} · {p.user_name} <span className="text-xs text-slate-500">({p.source})</span>
                  <span data-testid={`prediction-chart-${p.id}`} className={`ml-2 text-[10px] rounded-full border px-2 py-0.5 ${p.chart_based ? "text-emerald-300 border-emerald-500/30" : "text-slate-400 border-slate-500/30"}`}>{p.chart_based ? "Chart-based" : "No chart data"}</span></p>
                <p className="text-sm text-slate-400 truncate max-w-lg">{p.summary}</p>
                <div className="mt-1"><StatusPill s={p.status} /></div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {(p.status === "draft" || p.status === "rejected") && (
                  <button data-testid={`approve-${p.id}`} onClick={() => act(p.id, "approve")} disabled={busy === p.id + "approve"}
                    className="btn-ghost-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><CheckCircle2 size={14} /> Approve</button>
                )}
                {p.status === "draft" && (
                  <button onClick={() => act(p.id, "reject")} className="rounded-full px-4 py-2 text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1.5"><XCircle size={14} /> Reject</button>
                )}
                {(p.status === "approved" || p.status === "delivered") && (
                  <button data-testid={`deliver-${p.id}`} onClick={() => act(p.id, "deliver")} disabled={busy === p.id + "deliver"}
                    className="btn-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5">
                    {busy === p.id + "deliver" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {p.status === "delivered" ? "Resend" : "Deliver"}
                  </button>
                )}
                {p.status === "delivered" && <span className="text-xs text-emerald-300 flex items-center gap-1"><BadgeCheck size={14} /> Sent</span>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

function RemediesTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ category: "Mantra", text: "" });
  const load = () => api.get("/admin/remedies").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.text) return;
    await api.post("/admin/remedies", f); setF({ ...f, text: "" }); toast.success("Remedy template added"); load();
  };
  const del = async (id) => { await api.delete(`/admin/remedies/${id}`); load(); };
  return (
    <>
      <Head title="Remedy Templates" sub="Reusable remedies by category" />
      <Card className="mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="rounded-xl bg-[#0B1024] border border-amber-500/20 px-4 py-2.5 text-sm">
            {["Mantra", "Meditation", "Charity", "Lifestyle", "Spiritual", "Auspicious Activity", "Traditional"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="admin-remedy-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Remedy suggestion…"
            className="flex-1 rounded-xl bg-[#0B1024] border border-amber-500/20 px-4 py-2.5 text-sm" />
          <button data-testid="admin-remedy-add" onClick={add} className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-1.5"><Plus size={15} /> Add</button>
        </div>
      </Card>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="admin-remedies-list">
        {rows.map((r) => (
          <Card key={r.id}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="text-[11px] uppercase tracking-wider text-amber-400/90">{r.category}</p><p className="mt-1 text-sm text-slate-300">{r.text}</p></div>
              <button onClick={() => del(r.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

function CommunicationsTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/delivery-logs").then((r) => setLogs(r.data)); }, []);
  return (
    <>
      <Head title="Communications" sub="Email & WhatsApp delivery logs" />
      <Card className="mb-5">
        <p className="text-sm text-slate-300">Predictions are delivered via <span className="text-amber-200">Email (Resend, live)</span> and <span className="text-amber-200">WhatsApp (mock — connect an approved WhatsApp Business API provider to go live)</span>. Consent is captured at signup and editable per user.</p>
      </Card>
      <div className="overflow-x-auto mc-card p-0" data-testid="admin-delivery-logs">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-amber-400/80 border-b border-amber-500/15"><Th>Time</Th><Th>Email</Th><Th>WhatsApp</Th></tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-amber-500/8">
                <Td>{fmtDateTime(l.timestamp)}</Td><Td><StatusPill s={l.email_status} /></Td><Td><StatusPill s={l.whatsapp_status} /></Td>
              </tr>
            ))}
            {logs.length === 0 && <tr><Td colSpan={3}><span className="text-slate-500">No deliveries yet. Deliver a prediction from the Predictions tab.</span></Td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LuckyDrawTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/lucky-draw").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;
  const { counts, entries, draws, config } = data;

  const run = async () => {
    setBusy(true);
    try { const { data: d } = await api.post("/admin/lucky-draw/run"); toast.success(`Draw complete — winner: ${d.winner_name}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const publish = async (id) => { await api.post(`/admin/lucky-draw/${id}/publish`); toast.success("Winner published"); load(); };

  return (
    <>
      <Head title="24-Month Lucky Draw" sub={`Prize: ${config.lucky_draw_prize} · Eligibility: ${config.lucky_draw_eligibility_months} months`} />
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6" data-testid="admin-luckydraw-counts">
        {[["Total Entries", counts.total], ["Eligible", counts.eligible], ["Pending", counts.pending], ["Disqualified", counts.disqualified]].map(([l, v]) => (
          <Card key={l}><p className="font-serif text-3xl text-slate-50">{v}</p><p className="text-xs text-slate-400 mt-1">{l}</p></Card>
        ))}
      </div>

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-sm text-slate-300">Run an auditable randomized draw among eligible entries. A seed and audit log are recorded; the winner is published only after verification.</p></div>
          <button data-testid="admin-run-draw" onClick={run} disabled={busy || counts.eligible === 0}
            className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 shrink-0 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Run Draw ({counts.eligible} eligible)
          </button>
        </div>
      </Card>

      {draws.length > 0 && (
        <div className="mb-6">
          <h3 className="font-serif text-xl text-slate-50 mb-3">Draw Results</h3>
          <div className="space-y-3" data-testid="admin-draws">
            {draws.map((d) => (
              <Card key={d.id}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-slate-100">🏆 Winner: <span className="text-amber-200">{d.winner_name}</span> · Entry {d.winner_entry_id}</p>
                    <p className="text-xs text-slate-500 mt-1">Prize: {d.prize} · {fmtDateTime(d.draw_date)} · <StatusPill s={d.status} /></p>
                    <p className="text-[11px] text-slate-600 mt-1">Method: {d.randomization_method} · Seed: {d.random_seed?.slice(0, 12)}… · Eligible: {d.eligible_count}</p>
                  </div>
                  {!d.published && (
                    <button data-testid={`publish-${d.id}`} onClick={() => publish(d.id)} className="btn-ghost-gold rounded-full px-5 py-2 text-xs shrink-0 flex items-center gap-1.5"><BadgeCheck size={14} /> Verify & Publish</button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-serif text-xl text-slate-50 mb-3">Entries</h3>
      <div className="overflow-x-auto mc-card p-0" data-testid="admin-entries-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-amber-400/80 border-b border-amber-500/15"><Th>Entry ID</Th><Th>User</Th><Th>Months</Th><Th>Chances</Th><Th>Status</Th></tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.entry_number} className="border-b border-amber-500/8">
                <Td className="font-mono text-xs">{e.entry_number}</Td>
                <Td><span className="text-slate-200">{e.user_name}</span><br /><span className="text-xs text-slate-500">{e.user_email}</span></Td>
                <Td>{e.subscription_months}</Td><Td>{e.chances || 1}</Td><Td><StatusPill s={e.eligibility_status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PaymentsTab() {
  const [pays, setPays] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setPays(r.data)); }, []);
  const total = pays.filter((p) => p.status === "success").reduce((a, p) => a + p.amount, 0);
  return (
    <>
      <Head title="Payments" sub={`₹${total.toLocaleString("en-IN")} collected · ${pays.length} transactions`} />
      <div className="overflow-x-auto mc-card p-0" data-testid="admin-payments-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-amber-400/80 border-b border-amber-500/15"><Th>Date</Th><Th>User</Th><Th>Amount</Th><Th>Status</Th><Th>Invoice</Th></tr></thead>
          <tbody>
            {pays.map((p) => (
              <tr key={p.id} className="border-b border-amber-500/8">
                <Td>{fmtDate(p.payment_date)}</Td><Td>{p.user_name}</Td><Td>₹{p.amount}</Td><Td><StatusPill s={p.status} /></Td><Td className="font-mono text-xs">{p.invoice_reference}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ConfigTab() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setC(r.data)); }, []);
  if (!c) return <Loading />;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    try {
      await api.put("/admin/config", {
        monthly_price: parseInt(c.monthly_price),
        gst_percent: parseFloat(c.gst_percent),
        consultation_base_fee: parseInt(c.consultation_base_fee),
        lucky_draw_eligibility_months: parseInt(c.lucky_draw_eligibility_months),
        lucky_draw_prize: c.lucky_draw_prize,
        lucky_draw_prize_type: c.lucky_draw_prize_type,
        lucky_draw_date: c.lucky_draw_date,
        lucky_draw_status: c.lucky_draw_status,
        promo_banner: c.promo_banner,
      });
      toast.success("Configuration saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const field = "w-full rounded-xl bg-[#0B1024] border border-amber-500/20 px-4 py-2.5 text-sm mt-1.5";
  const lbl = "text-xs uppercase tracking-[0.15em] text-amber-400/90";
  return (
    <>
      <Head title="Configuration" sub="Pricing, lucky draw & promotional settings" />
      <Card className="max-w-2xl space-y-4" data-testid="admin-config-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Monthly Price (₹)</label><input data-testid="config-price" type="number" value={c.monthly_price} onChange={set("monthly_price")} className={field} /></div>
          <div><label className={lbl}>Lucky Draw Eligibility (months)</label><input data-testid="config-elig-months" type="number" value={c.lucky_draw_eligibility_months} onChange={set("lucky_draw_eligibility_months")} className={field} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Prize Type</label>
            <select data-testid="config-prize-type" value={c.lucky_draw_prize_type} onChange={set("lucky_draw_prize_type")} className={field}>
              <option value="apartment">1 BHK Apartment</option><option value="plot">Plot of Land</option><option value="other">Other Approved Prize</option>
            </select>
          </div>
          <div><label className={lbl}>Prize Label</label><input data-testid="config-prize-label" value={c.lucky_draw_prize} onChange={set("lucky_draw_prize")} className={field} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Draw Date</label><input data-testid="config-draw-date" type="date" value={c.lucky_draw_date || ""} onChange={set("lucky_draw_date")} className={field} /></div>
          <div>
            <label className={lbl}>Draw Status</label>
            <select data-testid="config-draw-status" value={c.lucky_draw_status} onChange={set("lucky_draw_status")} className={field}>
              <option value="open">Open</option><option value="closed">Closed</option><option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>GST (%)</label><input data-testid="config-gst" type="number" value={c.gst_percent ?? 18} onChange={set("gst_percent")} className={field} /></div>
          <div><label className={lbl}>Consultation Fee (₹)</label><input data-testid="config-consult-fee" type="number" value={c.consultation_base_fee ?? 999} onChange={set("consultation_base_fee")} className={field} /></div>
        </div>
        <div><label className={lbl}>Promotional Banner</label><input data-testid="config-promo" value={c.promo_banner || ""} onChange={set("promo_banner")} className={field} placeholder="Optional site-wide banner text" /></div>
        <button data-testid="config-save" onClick={save} className="btn-gold rounded-full px-6 py-3 text-sm">Save Configuration</button>
      </Card>
    </>
  );
}

const CONSULT_STATUSES = ["payment_pending", "confirmed", "awaiting_assignment", "assigned", "contacted", "scheduled", "completed", "cancelled", "refunded"];

function PlansTab() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  if (!plans) return <Loading />;
  const upd = (id, k, v) => setPlans(plans.map((p) => p.id === id ? { ...p, [k]: v } : p));
  const save = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, { name: p.name, price: parseInt(p.price), validity_months: parseInt(p.validity_months), per_question_price: parseFloat(p.per_question_price), active: p.active });
      toast.success(`${p.name} saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const f = "w-full rounded-lg bg-[#0B1024] border border-amber-500/20 px-2.5 py-1.5 text-sm mt-1";
  return (
    <>
      <Head title="Subscription Plans" sub="Edit prices, validity and per-question rates (launch prices are editable)" />
      <div className="grid gap-4 md:grid-cols-2" data-testid="admin-plans">
        {plans.map((p) => (
          <Card key={p.id} data-testid={`admin-plan-${p.id}`}>
            <div className="flex items-center justify-between mb-3"><span className="text-amber-200 font-medium">{p.name}</span><StatusPill s={p.active ? "active" : "pending"} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] text-slate-400">Price (₹)</label><input type="number" value={p.price} onChange={(e) => upd(p.id, "price", e.target.value)} className={f} data-testid={`plan-price-${p.id}`} /></div>
              <div><label className="text-[11px] text-slate-400">Validity (months)</label><input type="number" value={p.validity_months} onChange={(e) => upd(p.id, "validity_months", e.target.value)} className={f} /></div>
              <div><label className="text-[11px] text-slate-400">₹ / question</label><input type="number" value={p.per_question_price} onChange={(e) => upd(p.id, "per_question_price", e.target.value)} className={f} data-testid={`plan-qprice-${p.id}`} /></div>
              <label className="text-[11px] text-slate-400 flex items-center gap-2 mt-6"><input type="checkbox" checked={p.active} onChange={(e) => upd(p.id, "active", e.target.checked)} /> Active</label>
            </div>
            <button onClick={() => save(p)} className="btn-gold rounded-full px-5 py-2 text-sm mt-4" data-testid={`plan-save-${p.id}`}>Save Plan</button>
          </Card>
        ))}
      </div>
    </>
  );
}

function QuestionsTab() {
  const [qs, setQs] = useState(null);
  const [draft, setDraft] = useState({});
  const load = () => api.get("/admin/questions").then((r) => setQs(r.data));
  useEffect(() => { load(); }, []);
  if (!qs) return <Loading />;
  const answer = async (id) => {
    if (!draft[id]) { toast.error("Enter an answer"); return; }
    try { await api.post(`/admin/questions/${id}/answer`, { answer_text: draft[id] }); toast.success("Answer published"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Head title="Customised Questions" sub="Review paid questions and publish answers" />
      <div className="space-y-4" data-testid="admin-questions">
        {qs.length === 0 && <Card>No questions yet.</Card>}
        {qs.map((q) => (
          <Card key={q.id} data-testid={`admin-question-${q.id}`}>
            <div className="flex items-center justify-between mb-2"><span className="text-xs text-slate-400">{q.user?.name} · {q.user?.email}</span><StatusPill s={q.status === "answered" ? "delivered" : "pending"} /></div>
            <p className="text-slate-200">{q.question_text}</p>
            <p className="text-[11px] text-slate-500 mt-1">Paid ₹{q.amount} · {fmtDateTime(q.created_at)}
              {q.answer_source && <span data-testid={`question-source-${q.id}`} className="ml-2 text-amber-300/90">· {q.answer_source === "astrologer" ? `Answered by ${q.astrologer}` : q.chart_based ? "Auto: FreeAstrologyAPI chart + AI" : "Auto: AI (no chart data)"}</span>}</p>
            {q.status === "answered" && (
              <div className="mt-3 rounded-lg bg-amber-500/5 border border-amber-500/15 px-3 py-2 text-sm text-slate-300 whitespace-pre-line">{q.answer_text}</div>
            )}
            {(
              <div className="mt-3">
                <textarea rows={2} value={draft[q.id] ?? ""} onChange={(e) => setDraft({ ...draft, [q.id]: e.target.value })} className="w-full rounded-lg bg-[#0B1024] border border-amber-500/20 px-3 py-2 text-sm" placeholder="Write the astrologer's answer…" data-testid={`answer-input-${q.id}`} />
                <button onClick={() => answer(q.id)} className="btn-gold rounded-full px-5 py-2 text-sm mt-2" data-testid={`answer-submit-${q.id}`}>{q.status === "answered" ? "Replace Answer" : "Publish Answer"}</button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function ConsultationsTab() {
  const [cs, setCs] = useState(null);
  const [draft, setDraft] = useState({});
  const load = () => api.get("/admin/consultations").then((r) => setCs(r.data));
  useEffect(() => { load(); }, []);
  if (!cs) return <Loading />;
  const update = async (id, body) => {
    try { await api.post(`/admin/consultations/${id}/update`, body); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Head title="Astrologer Consultations" sub="Assign astrologers and track the 24-hour response deadline" />
      <div className="space-y-4" data-testid="admin-consultations">
        {cs.length === 0 && <Card>No bookings yet.</Card>}
        {cs.map((c) => (
          <Card key={c.id} data-testid={`admin-consult-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-amber-200">{c.topic}</span>
              {c.overdue ? <span className="text-[11px] text-red-300">⚠ Deadline passed</span> : <StatusPill s={c.status === "completed" ? "delivered" : "pending"} />}
            </div>
            <p className="text-xs text-slate-400">{c.name} · {c.phone} · {c.preferred_contact} · {c.user_email}</p>
            {c.birth_details && <p className="text-xs text-slate-500 mt-1">Birth: {c.birth_details}</p>}
            <p className="text-[11px] text-slate-500 mt-1">Paid ₹{c.amount} · Deadline {fmtDateTime(c.response_deadline)} · <span className="text-amber-200 capitalize">{c.status.replace(/_/g, " ")}</span></p>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <input value={draft[c.id] ?? (c.astrologer || "")} onChange={(e) => setDraft({ ...draft, [c.id]: e.target.value })} placeholder="Astrologer name" className="rounded-lg bg-[#0B1024] border border-amber-500/20 px-2.5 py-1.5 text-sm" data-testid={`consult-astro-${c.id}`} />
              <button onClick={() => update(c.id, { astrologer: draft[c.id] || c.astrologer || "Astrologer" })} className="btn-ghost-gold rounded-full px-4 py-1.5 text-xs">Assign</button>
              <select value={c.status} onChange={(e) => update(c.id, { status: e.target.value })} className="rounded-lg bg-[#0B1024] border border-amber-500/20 px-2.5 py-1.5 text-sm" data-testid={`consult-status-${c.id}`}>
                {CONSULT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

const EMPTY_ASTRO = { name: "", title: "", experience_years: 0, languages: [], specialities: [], bio: "", photo: "", active: true, order: 99 };

const resizePhoto = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = 320, c = document.createElement("canvas");
    const r = Math.max(s / img.width, s / img.height);
    c.width = s; c.height = s;
    c.getContext("2d").drawImage(img, (s - img.width * r) / 2, (s - img.height * r) / 2, img.width * r, img.height * r);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

function AstrologersTab() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/astrologers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  if (!list) return <Loading />;
  return (
    <>
      <Head title="Astrologer Profiles" sub="Shown on the Home page consultation section. Replace [placeholder] details with real ones." />
      <div className="grid gap-4 md:grid-cols-2" data-testid="admin-astrologers">
        {list.map((a) => <AstroForm key={a.id} initial={a} onDone={load} />)}
        <AstroForm key={`new-${list.length}`} initial={EMPTY_ASTRO} onDone={load} isNew />
      </div>
    </>
  );
}

function AstroForm({ initial, onDone, isNew }) {
  const [a, setA] = useState({ ...initial, languages: initial.languages.join(", "), specialities: initial.specialities.join(", ") });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const tid = isNew ? "new" : initial.id;
  const f = "w-full rounded-lg bg-[#0B1024] border border-amber-500/20 px-2.5 py-1.5 text-sm mt-1";
  const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setA({ ...a, photo: await resizePhoto(file) }); } catch { toast.error("Could not read image"); }
  };
  const save = async () => {
    if (!a.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = { ...a, experience_years: parseInt(a.experience_years) || 0, order: parseInt(a.order) || 99, languages: split(a.languages), specialities: split(a.specialities) };
    delete body.id; delete body.created_at;
    try {
      if (isNew) await api.post("/admin/astrologers", body); else await api.put(`/admin/astrologers/${initial.id}`, body);
      toast.success(`${a.name} saved`); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${initial.name}?`)) return;
    try { await api.delete(`/admin/astrologers/${initial.id}`); toast.success("Deleted"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card data-testid={`admin-astro-${tid}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-14 w-14 rounded-full overflow-hidden border border-amber-400/40 bg-[#0B1024] flex items-center justify-center text-amber-200 shrink-0">
          {a.photo ? <img src={a.photo} alt="" className="h-full w-full object-cover" /> : <UserPlus size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-amber-200 font-medium truncate">{isNew ? "Add new astrologer" : initial.name}</p>
          <label className="text-[11px] text-amber-300 cursor-pointer hover:underline">
            Upload photo<input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid={`astro-photo-${tid}`} />
          </label>
          {a.photo && <button onClick={() => setA({ ...a, photo: "" })} className="ml-3 text-[11px] text-slate-400 hover:text-red-300">Remove</button>}
        </div>
        {!isNew && <StatusPill s={a.active ? "active" : "draft"} />}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[11px] text-slate-400">Name</label><input value={a.name} onChange={set("name")} className={f} data-testid={`astro-name-${tid}`} /></div>
        <div><label className="text-[11px] text-slate-400">Title</label><input value={a.title} onChange={set("title")} className={f} placeholder="Vedic Astrologer" data-testid={`astro-title-${tid}`} /></div>
        <div><label className="text-[11px] text-slate-400">Experience (years)</label><input type="number" value={a.experience_years} onChange={set("experience_years")} className={f} data-testid={`astro-exp-${tid}`} /></div>
        <div><label className="text-[11px] text-slate-400">Display order</label><input type="number" value={a.order} onChange={set("order")} className={f} data-testid={`astro-order-${tid}`} /></div>
        <div className="col-span-2"><label className="text-[11px] text-slate-400">Languages (comma separated)</label><input value={a.languages} onChange={set("languages")} className={f} data-testid={`astro-langs-${tid}`} /></div>
        <div className="col-span-2"><label className="text-[11px] text-slate-400">Specialities (comma separated)</label><input value={a.specialities} onChange={set("specialities")} className={f} data-testid={`astro-specs-${tid}`} /></div>
        <div className="col-span-2"><label className="text-[11px] text-slate-400">Short bio</label><textarea rows={2} value={a.bio} onChange={set("bio")} className={f} data-testid={`astro-bio-${tid}`} /></div>
        <label className="text-[11px] text-slate-400 flex items-center gap-2"><input type="checkbox" checked={a.active} onChange={set("active")} data-testid={`astro-active-${tid}`} /> Show on website</label>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={busy} className="btn-gold rounded-full px-5 py-2 text-sm disabled:opacity-60" data-testid={`astro-save-${tid}`}>{isNew ? "Add Astrologer" : "Save"}</button>
        {!isNew && <button onClick={remove} className="text-slate-400 hover:text-red-300 text-sm flex items-center gap-1" data-testid={`astro-delete-${tid}`}><Trash2 size={14} /> Delete</button>}
      </div>
    </Card>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "", ...p }) => <td className={`px-4 py-3 align-top ${className}`} {...p}>{children}</td>;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const Loading = () => <div className="flex items-center gap-2 text-amber-200/80 py-16"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

function StatusPill({ s }) {
  const map = {
    active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    success: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    sent: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    delivered: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    published: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    approved: "text-amber-200 bg-amber-500/10 border-amber-500/30",
    eligible: "text-amber-200 bg-amber-500/10 border-amber-500/30",
    mock: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    draft: "text-slate-300 bg-slate-500/10 border-slate-500/30",
    pending: "text-slate-300 bg-slate-500/10 border-slate-500/30",
    pending_verification: "text-amber-200 bg-amber-500/10 border-amber-500/30",
    cancelled: "text-red-300 bg-red-500/10 border-red-500/30",
    disqualified: "text-red-300 bg-red-500/10 border-red-500/30",
    rejected: "text-red-300 bg-red-500/10 border-red-500/30",
    failed: "text-red-300 bg-red-500/10 border-red-500/30",
    skipped: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  };
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] ${map[s] || "text-slate-300 bg-slate-500/10 border-slate-500/30"}`}>{s}</span>;
}
